/**
 * Usage and quota — what the dashboard reads. Ported from `bimgo-ai-web`'s
 * `usageApi`.
 *
 * Four endpoints on **api-v2** (not the agent; this is billing data, not a local
 * turn), all behind the `{ data }` envelope:
 *
 *   - `GET /usages/status`                                → the caller's cycle
 *   - `GET /usages/timeseries?days=N` *or* `?from=&to=`   → their daily trend
 *   - `GET /organizations/{id}/usages`                    → every member's usage
 *   - `GET /organizations/{id}/usage-dashboard`           → the org's analytics
 *   - `GET /organizations/{id}/usages/timeseries?days=N`  → the org's trend
 *
 * The org **trend** endpoint is what the org tab used to draw; `usage-dashboard`
 * replaced it there and answers with the current cycle, a selected range and the
 * range's daily points in one request. The older call is kept because api-v2
 * still serves it and `bimgo-ai-web` still exports it.
 *
 * The two org calls are **org-admin only** and api-v2 enforces it. The dashboard
 * hides that half unless the caller holds `org:read`, which is a UX accelerator:
 * showing the tab and then a 403 is worse than not showing it, but the server is
 * what actually decides.
 *
 * Three shapes to respect rather than tidy:
 *
 *   - **A null limit means unlimited, not zero.** `max_tokens_per_month` is
 *     nullable, and so is the matching `remaining` field. Coercing null to 0
 *     turns an unlimited plan into an exhausted one — the reader would think
 *     they were out of quota.
 *   - **Requests are counted, never capped.** The monthly request cap was
 *     removed (bimgo-ai-api-v3#32): `max_requests_per_month` and
 *     `requests_remaining_this_month` stay on the wire but are always null, and
 *     the dashboard shows request counts with no denominator.
 *   - **A null plan cap is not the same as no ceiling.** On an `org_shared`
 *     plan api-v2 withholds the token cap, because it does not describe a
 *     member there: it is a ceiling on pool draws rather than an allowance.
 *     The member is still capped — by
 *     their own `limit` / `monthly_credit_limit` — so reading a withheld plan
 *     cap as "unlimited" tells a capped member they have no ceiling, which is
 *     the same lie as reading null as zero, in the other direction.
 *   - **`email` on a member row is nullable.** The backend may fail to read
 *     `auth.users` depending on deployment grants; the row still has real usage
 *     numbers on it, so it degrades to the user id rather than being dropped.
 *
 * **`tokens_*` is BIMGO credits, not raw provider tokens**, and counts *both*
 * funding sources — the monthly allowance and the org wallet. The field names
 * predate the credit ledger and api-v2 keeps them for client compatibility, so
 * the name is the one thing here that cannot be taken at face value. The word on
 * screen stays "tokens", as it does in `bimgo-ai-web`.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";

/** Which plan shape an organization is on. Shared with `lib/tokens.ts`. */
export type BillingMode = "per_seat" | "org_shared";

export interface UsagePlanInfo {
  name: string;
  /**
   * Which mode the *organization* is on — and therefore whether the two caps
   * below describe this member at all. `/usages/status` only knows it if the
   * caller says which org they are asking about; see `getUsageStatus`.
   */
  billing_mode: BillingMode;
  /** Always null — the request cap is no longer enforced. See the header. */
  max_requests_per_month: number | null;
  /**
   * Null means **no cap on this plan** — which is "unlimited" on a per-seat
   * plan and "withheld, look at `limit`" on `org_shared`. See the header.
   */
  max_tokens_per_month: number | null;
  /** The plan's timezone — the server bucketed days against it. */
  timezone: string;
}

/**
 * The caller's **own** cap on wallet (pay-as-you-go) spend this cycle.
 *
 * Self-scoped, so a plain member may read it: it says nothing about anyone
 * else. This is the ceiling that actually gates a member in either mode, and
 * the only one they have on `org_shared`, where the plan caps are withheld.
 *
 * `spent` counts **wallet draws only**, so on a per-seat plan it sits at 0
 * until the prepaid allowance runs out — unlike `UsageStats.tokens_used_this_month`,
 * which spans both sources. Two figures, two meanings; showing one under the
 * other's label is the trap this comment exists for.
 */
export interface UsageLimitInfo {
  /** Null = unlimited. */
  effective_limit: number | null;
  limit_source: "member" | "default" | "unlimited";
  spent: number;
  /** Null when `effective_limit` is null — nothing to count down from. */
  remaining: number | null;
}

export interface UsageStats {
  requests_used_this_month: number;
  /** Always null — the request cap is no longer enforced. See the header. */
  requests_remaining_this_month: number | null;
  tokens_used_this_month: number;
  tokens_remaining_this_month: number | null;
}

export interface UsageCycle {
  current_date: string;
  monthly_start: string;
  monthly_end: string;
}

export interface UsageStatus {
  plan: UsagePlanInfo;
  usage: UsageStats;
  /** Absent on older api-v2 builds; null then, never invented. */
  limit: UsageLimitInfo | null;
  usage_cycle: UsageCycle;
}

export interface UsagePoint {
  date: string;
  requests: number;
  tokens: number;
}

export interface UsageTimeseries {
  timezone: string;
  days: number;
  points: UsagePoint[];
}

export interface OrgUserUsage {
  user_id: string;
  /** Null when the backend could not read `auth.users` — see the header. */
  email: string | null;
  requests_this_month: number;
  /** BIMGO credits across both funding sources — see the header. */
  tokens_this_month: number;
  /**
   * **This member's own** resolved cap (`user_plans.monthly_credit_limit`);
   * null = unlimited. On `org_shared` the plan caps are withheld, so this is
   * the only ceiling a row has to be measured against — and unlike the plan
   * cap it differs per row, so a table cannot divide everyone by one number.
   */
  monthly_credit_limit: number | null;
}

export interface OrgUsageSummary {
  plan: UsagePlanInfo | null;
  usage_cycle: { monthly_start: string; monthly_end: string } | null;
  users: OrgUserUsage[];
}

/** The ranges the trend offers, as the reference offers them. */
export const RANGES = [7, 14, 30, 90] as const;
export type Range = (typeof RANGES)[number];

/**
 * A custom window, as api-v2 takes it: **`to` is exclusive**.
 *
 * Both ends are plain calendar dates (`2026-09-01`), never timestamps — the
 * server buckets by day in the plan's timezone, and a time component would
 * invite the client to think it can ask for half a day.
 */
export interface UsageDateRange {
  from: string;
  to: string;
}

/** What the trend is showing: one of the presets, or a window. */
export type TrendSelection = Range | UsageDateRange;

// -- calendar arithmetic -----------------------------------------------------
//
// Every date here is a **date-only string**, and the whole family exists to
// stop that string being re-interpreted in the reader's timezone. `new
// Date("2026-09-01")` is UTC midnight, which is the previous day anywhere west
// of Greenwich — so a range that starts on the 1st would be labelled the 31st
// for a reader in New York. Pinning to **UTC noon** puts the instant far enough
// from both boundaries that no offset can move the calendar day, and every
// formatter below then reads it back in UTC.

/** `"2026-09-01"` → a Date at UTC noon on that day. */
export function dateOnlyToUtc(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1, 12));
}

/** The inverse: a Date → `"2026-09-01"`, read in UTC. */
export function toDateOnly(value: Date): string {
  return [
    value.getUTCFullYear(),
    String(value.getUTCMonth() + 1).padStart(2, "0"),
    String(value.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

/** One day later — how an inclusive last day becomes an exclusive `to`. */
export function addOneDay(value: string): string {
  const date = dateOnlyToUtc(value);
  date.setUTCDate(date.getUTCDate() + 1);
  return toDateOnly(date);
}

/**
 * The window a preset means, ending on the server's own `as_of` day.
 *
 * Anchored on the server's date rather than the reader's clock: a machine whose
 * calendar is a day ahead would otherwise ask for a day the server has no data
 * for and be told, correctly and unhelpfully, that nothing happened.
 */
export function presetRange(asOf: string, days: Range): UsageDateRange {
  const today = dateOnlyToUtc(asOf);
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - days + 1);
  const end = new Date(today);
  end.setUTCDate(end.getUTCDate() + 1);
  return { from: toDateOnly(start), to: toDateOnly(end) };
}

/** The longest window api-v2 will answer for. */
export const MAX_RANGE_DAYS = 366;

/**
 * Why a hand-picked range cannot be used, or null when it can.
 *
 * Returns a **locale key**, not a sentence, so the rule lives here and the
 * wording lives in the dictionary — the same 366-day ceiling the reference
 * enforces, and the same two failures.
 */
export function rangeError(from: string | null, to: string | null): string | null {
  if (!from || !to) return "dashboard.analytics.rangeIncomplete";
  const start = dateOnlyToUtc(from);
  const end = dateOnlyToUtc(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "dashboard.analytics.rangeIncomplete";
  }
  if (end.getTime() < start.getTime()) return "dashboard.analytics.rangeIncomplete";
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  return days > MAX_RANGE_DAYS ? "dashboard.analytics.rangeTooLong" : null;
}

/**
 * `"Sep 1, 2026 - Sep 30, 2026"` for a half-open range.
 *
 * The last day is `to` **minus one**, because `to` is exclusive: printing it
 * raw would advertise a day the figures do not include.
 */
export function formatDateRange(from: string, to: string, locale: string): string {
  const start = dateOnlyToUtc(from);
  const end = dateOnlyToUtc(to);
  end.setUTCDate(end.getUTCDate() - 1);
  const formatter = new Intl.DateTimeFormat(locale === "zh" ? "zh-TW" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

async function get(
  url: string,
  token: string | null,
  fetchImpl: typeof fetch,
): Promise<unknown> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  let response: Response;
  try {
    response = await fetchImpl(url, { headers: { Authorization: bearer(token) } });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }
  if (response.status === 401) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = readDetail(body);
    // 403 keeps its status: on the org calls it means "not an org admin", which
    // the dashboard reports as a permission problem rather than signing the
    // user out of a session that is working fine.
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return (body as { data?: unknown } | null)?.data;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, "");
}

/**
 * The caller's own cycle.
 *
 * **`orgId` is not optional in practice.** A user can belong to several
 * organizations, and it is the org's plan that decides the billing mode; asked
 * without one, api-v2 falls back to `per_seat` and answers with plan caps that
 * do not govern a member of an `org_shared` org at all. The desktop always
 * knows which org the shell is working in, so it always says.
 */
export async function getUsageStatus(
  token: string | null,
  orgId: string | null,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<UsageStatus | null> {
  const suffix = orgId ? `?organization_id=${encodeURIComponent(orgId)}` : "";
  const data = await get(
    `${base(deps.apiBaseUrl)}/usages/status${suffix}`,
    token,
    deps.fetchImpl ?? fetch,
  );
  return isStatus(data) ? status(data) : null;
}

/**
 * The caller's daily trend, over a preset window **or** a hand-picked one.
 *
 * The two are one endpoint with different parameters — `days`, or `from`/`to` —
 * and never both: sending both would leave the server to choose, and which one
 * it chose would be invisible on screen.
 */
export async function getUsageTimeseries(
  token: string | null,
  selection: TrendSelection,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<UsageTimeseries> {
  const query = new URLSearchParams();
  if (typeof selection === "number") query.set("days", String(selection));
  else {
    query.set("from", selection.from);
    query.set("to", selection.to);
  }
  const data = await get(
    `${base(deps.apiBaseUrl)}/usages/timeseries?${query.toString()}`,
    token,
    deps.fetchImpl ?? fetch,
  );
  return series(data, typeof selection === "number" ? selection : 0);
}

export async function getOrgUsage(
  token: string | null,
  orgId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<OrgUsageSummary> {
  const data = await get(
    `${base(deps.apiBaseUrl)}/organizations/${encodeURIComponent(orgId)}/usages`,
    token,
    deps.fetchImpl ?? fetch,
  );
  const d = (data ?? {}) as Partial<OrgUsageSummary>;
  return {
    plan: d.plan ?? null,
    usage_cycle: d.usage_cycle ?? null,
    // `monthly_credit_limit` is absent on an older api-v2; null, never 0 —
    // an invented zero would report every member as already exhausted.
    users: Array.isArray(d.users)
      ? d.users.map((u) => ({ ...u, monthly_credit_limit: u.monthly_credit_limit ?? null }))
      : [],
  };
}

export async function getOrgUsageTimeseries(
  token: string | null,
  orgId: string,
  days: Range,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<UsageTimeseries> {
  const data = await get(
    `${base(deps.apiBaseUrl)}/organizations/${encodeURIComponent(orgId)}/usages/timeseries?days=${days}`,
    token,
    deps.fetchImpl ?? fetch,
  );
  return series(data, days);
}

// -- the organisation's analytics --------------------------------------------
//
// `GET /organizations/{id}/usage-dashboard` — the org tab's own endpoint, and
// the one place the two halves of the question are answered together: where the
// org stands in the **current cycle**, and what it did over a **selected range**
// (a preset window, or one an admin picked), points included.
//
// **`credits` is BIMGO credits.** It replaced a `raw_tokens` field: raw provider
// token counts are an internal accounting detail and are no longer exposed. The
// trend charts take it as their second series, which is why the point is mapped
// onto `tokens` before it reaches them — one chart component, two callers.

export interface OrgUsageDashboardPoint {
  date: string;
  requests: number;
  credits: number;
}

export interface OrgUsageDashboardCurrentCycle {
  from: string;
  to: string;
  requests: number;
  credits: number;
  active_users: number;
  cloud_skills_total: number;
}

export interface OrgUsageDashboardSelectedRange {
  selection: "previous_complete_cycle" | "custom";
  from: string;
  to: string;
  requests: number;
  credits: number;
  active_users: number;
  cloud_skills_created: number;
  points: OrgUsageDashboardPoint[];
}

export interface OrgUsageDashboard {
  timezone: string;
  /** The server's own "today" — what a preset window is measured back from. */
  as_of: string;
  current_cycle: OrgUsageDashboardCurrentCycle;
  selected_range: OrgUsageDashboardSelectedRange;
}

/**
 * The org's analytics. Omit `range` for the server's default selection (the
 * previous complete cycle) — which is also the call that reports `as_of`, and
 * therefore the one a preset window has to be built from.
 */
export async function getOrgUsageDashboard(
  token: string | null,
  orgId: string,
  range: UsageDateRange | null,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<OrgUsageDashboard | null> {
  const query = range ? `?from=${range.from}&to=${range.to}` : "";
  const data = await get(
    `${base(deps.apiBaseUrl)}/organizations/${encodeURIComponent(orgId)}/usage-dashboard${query}`,
    token,
    deps.fetchImpl ?? fetch,
  );
  return isDashboard(data) ? data : null;
}

function isDashboard(data: unknown): data is OrgUsageDashboard {
  return (
    typeof data === "object" &&
    data !== null &&
    "current_cycle" in data &&
    "selected_range" in data
  );
}

/**
 * The analytics points, as the trend charts want them.
 *
 * `credits` becomes `tokens` because that is the series name the chart and its
 * label already use. Renaming it at the boundary keeps one chart component for
 * both scopes; renaming it *inside* the chart would mean two.
 */
export function analyticsPoints(
  range: OrgUsageDashboardSelectedRange | null | undefined,
): UsagePoint[] {
  return (range?.points ?? []).map((p) => ({
    date: p.date,
    requests: p.requests,
    tokens: p.credits,
  }));
}

function isStatus(data: unknown): data is UsageStatus {
  // `limit` is deliberately not required: api-v2 added it after this client
  // shipped, and demanding it would turn every response from an older build
  // into "no active plan".
  return (
    typeof data === "object" &&
    data !== null &&
    "plan" in data &&
    "usage" in data &&
    "usage_cycle" in data
  );
}

function status(data: UsageStatus): UsageStatus {
  return {
    ...data,
    plan: { ...data.plan, billing_mode: data.plan?.billing_mode ?? "per_seat" },
    limit: data.limit ?? null,
  };
}

/**
 * `days` is what the response should say when the server does not: a custom
 * range has no preset length, so it falls back to 0 and nothing reads it.
 */
function series(data: unknown, days: number): UsageTimeseries {
  const d = (data ?? {}) as Partial<UsageTimeseries>;
  return {
    timezone: typeof d.timezone === "string" ? d.timezone : "UTC",
    days: typeof d.days === "number" ? d.days : days,
    points: Array.isArray(d.points) ? d.points : [],
  };
}

/**
 * How much of a quota is gone, 0–100 — or **null when there is no limit**.
 *
 * Null is the whole point: an unlimited plan has no bar to fill, and rendering
 * 0% would say "none used" while rendering 100% would say "exhausted". Neither
 * is true, so the caller shows a number without a bar.
 */
export function usedPercent(used: number, limit: number | null): number | null {
  if (limit === null || limit <= 0) return null;
  return Math.min(100, Math.floor((used / limit) * 100));
}

/** Never negative: an over-quota cycle reports 0 left, not a negative count. */
export function remaining(value: number | null): number | null {
  return value === null ? null : Math.max(0, value);
}

/** How the members table can be ordered, as the reference offers it. */
export const MEMBER_SORTS = [
  "requests",
  "tokens",
  "remainingTokens",
  "email",
] as const;
export type MemberSort = (typeof MEMBER_SORTS)[number];

/**
 * What a row shows when it has no email.
 *
 * The **same** function feeds the email comparator and the rendered cell, on
 * purpose: sorting on `email` while displaying a fallback would order rows by
 * strings that are not on screen, and a list that disagrees with its own sort is
 * worse than an unsorted one.
 */
export function memberLabel(user: OrgUserUsage): string {
  return user.email || `${user.user_id.slice(0, 8)}…`;
}

/**
 * A member's remaining allowance against a ceiling.
 *
 * Null means "no ceiling", which is different from zero left. The caller
 * decides *which* ceiling — see `memberTokenLimit`.
 */
export function remainingFor(used: number, limit: number | null): number | null {
  return limit === null ? null : Math.max(limit - (used || 0), 0);
}

/**
 * The credit ceiling **this row** is actually held to.
 *
 * On a per-seat plan that is the plan allowance, shared by every member. On
 * `org_shared` api-v2 withholds the plan cap and each member carries their own
 * `monthly_credit_limit` instead — so the arithmetic that used to look per-user
 * and was not, now genuinely is.
 *
 * One helper feeds the cell, the remaining figure and the comparator, so a
 * table can never sort by a ceiling different from the one it prints.
 */
export function memberTokenLimit(
  user: OrgUserUsage,
  planTokenLimit: number | null,
): number | null {
  return planTokenLimit ?? user.monthly_credit_limit ?? null;
}

/**
 * Does *any* row have a credit ceiling?
 *
 * It cannot key off the plan alone: on `org_shared` some members are capped and
 * others are not, so a plan-level test would hide a column that half the table
 * can fill.
 */
export function hasTokenCeiling(
  users: readonly OrgUserUsage[],
  planTokenLimit: number | null,
): boolean {
  return planTokenLimit !== null || users.some((u) => u.monthly_credit_limit !== null);
}

/**
 * Order the table. Numeric keys descend (biggest first); `email` ascends.
 *
 * **Rows with no ceiling sink to the bottom of a remaining-sort** rather than
 * sorting as zero: "nothing left" and "no limit to have left" are different
 * facts, and putting the second at the top of a "most remaining" list — or the
 * bottom of it — would state the wrong one.
 */
export function sortMembers(
  users: readonly OrgUserUsage[],
  key: MemberSort,
  limits: { tokens: number | null },
): OrgUserUsage[] {
  const copy = [...users];
  /**
   * Rows with no remaining go last.
   *
   * **The mixed case is now reachable.** It used to be dead code kept for a
   * per-user-limit future, on the reasoning that one org-wide cap makes
   * `remainingFor` null for every row or for none. `org_shared` is that future:
   * the plan cap is withheld and each row carries its own, so a table can hold
   * capped and uncapped members at once — and "no limit" must not sort as
   * "none left".
   */
  const nullsLast = (a: number | null, b: number | null): number | null => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return null;
  };

  switch (key) {
    case "requests":
      return copy.sort((a, b) => (b.requests_this_month || 0) - (a.requests_this_month || 0));
    case "tokens":
      return copy.sort((a, b) => (b.tokens_this_month || 0) - (a.tokens_this_month || 0));
    case "remainingTokens":
      return copy.sort((a, b) => {
        // Per row, not per plan — see `memberTokenLimit`.
        const ra = remainingFor(a.tokens_this_month, memberTokenLimit(a, limits.tokens));
        const rb = remainingFor(b.tokens_this_month, memberTokenLimit(b, limits.tokens));
        return nullsLast(ra, rb) ?? (rb as number) - (ra as number);
      });
    case "email":
      return copy.sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
  }
}

/** `14_012_345` → `14.0M`, for a chart axis that has to fit. */
export function compact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}
